// src/pages/Home/components/Navbar3/hooks/useNavbar3.js

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useUser } from "../../../UserContext";

import { useMessage } from "../../../../../hooks/MessageContext";
import { useContactForm } from "../../../../../hooks/ContactFormContext";
import { useVote } from "../../../../../hooks/VoteContext";
import { useConvocation } from "../../../../../hooks/ConvocationContext";

import { checkRoleAccess } from "../../../../../utils/roleHelper";

import { toAbsoluteUserPhotoUrl } from "../../../../../services/userPhotoServices";

import {
  CONVOCATION_ROLES,
} from "../data/navbar3.data";

import {
  getStoredUserInfo,
} from "../utils/navbar3.utils";

export default function useNavbar3() {
  const {
    user,
    updateUser,
  } = useUser();

  const {
    unreadMessageCount,
  } = useMessage();

  const {
    unreadCountContactForm,
  } = useContactForm();

  const {
    pendingVoteCount,
    pendingVotesLoading,
    refreshPendingVotes,
  } = useVote();

  const {
    pendingFutureConvocationCount,
    loadingConvocations,
    refreshPendingConvocations,
  } = useConvocation();

  const navigate = useNavigate();
  const location = useLocation();

  const [
    menuOpen,
    setMenuOpen,
  ] = useState(false);

  const [
    isDarkMode,
    setIsDarkMode,
  ] = useState(false);

  const userRoleId = Number(
    user?.role_id,
  );

  const avatarUrl = useMemo(() => {
    const photoPath =
      user?.photoURL ||
      user?.photo_url ||
      user?.photoUrl;

    return (
      toAbsoluteUserPhotoUrl(
        photoPath,
      ) ||
      "/img/user_avatar.jpg"
    );
  }, [
    user?.photoURL,
    user?.photo_url,
    user?.photoUrl,
  ]);

  const badgeCounts = useMemo(
    () => ({
      messages:
        Number(
          unreadMessageCount,
        ) || 0,

      votes:
        Number(
          pendingVoteCount,
        ) || 0,

      convocations:
        Number(
          pendingFutureConvocationCount,
        ) || 0,

      contactForms:
        Number(
          unreadCountContactForm,
        ) || 0,
    }),
    [
      pendingFutureConvocationCount,
      pendingVoteCount,
      unreadCountContactForm,
      unreadMessageCount,
    ],
  );

  useEffect(() => {
    if (user) {
      return;
    }

    const storedUserInfo =
      getStoredUserInfo();

    if (storedUserInfo) {
      updateUser(
        storedUserInfo,
      );
    }
  }, [
    user,
    updateUser,
  ]);

  useEffect(() => {
    const storedTheme =
      localStorage.getItem("theme");

    const systemPrefersDark =
      window.matchMedia?.(
        "(prefers-color-scheme: dark)",
      ).matches;

    const shouldUseDarkMode =
      storedTheme === "dark" ||
      (
        !storedTheme &&
        systemPrefersDark
      );

    document.documentElement
      .classList.toggle(
        "dark",
        shouldUseDarkMode,
      );

    setIsDarkMode(
      shouldUseDarkMode,
    );
  }, []);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    const previousBodyOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    const handleEscapeKey = (
      event,
    ) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    window.addEventListener(
      "keydown",
      handleEscapeKey,
    );

    return () => {
      document.body.style.overflow =
        previousBodyOverflow;

      window.removeEventListener(
        "keydown",
        handleEscapeKey,
      );
    };
  }, [menuOpen]);

  useEffect(() => {
    if (
      !menuOpen ||
      !user
    ) {
      return;
    }

    refreshPendingVotes?.();

    refreshPendingConvocations?.({
      silent: true,
    });
  }, [
    menuOpen,
    refreshPendingConvocations,
    refreshPendingVotes,
    user,
  ]);

  /*
   * Ferme également le panneau si la route
   * change avec les boutons précédent/suivant
   * du navigateur.
   */
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const hasAccessToItem =
    useCallback(
      (roles = []) => {
        if (
          !Array.isArray(roles) ||
          roles.length === 0
        ) {
          return true;
        }

        if (
          !Number.isFinite(
            userRoleId,
          )
        ) {
          return false;
        }

        return checkRoleAccess(
          userRoleId,
          roles,
        );
      },
      [userRoleId],
    );

  const handleNavigation =
    useCallback(
      (
        path,
        roleRequired = [],
      ) => {
        if (!path) {
          return;
        }

        if (
          !hasAccessToItem(
            roleRequired,
          )
        ) {
          return;
        }

        navigate(path);
        setMenuOpen(false);
      },
      [
        hasAccessToItem,
        navigate,
      ],
    );

  const handleLogout =
    useCallback(() => {
      localStorage.removeItem(
        "userInfo",
      );

      localStorage.removeItem(
        "token",
      );

      sessionStorage.removeItem(
        "token",
      );

      setMenuOpen(false);

      updateUser(null);

      navigate("/login");
    }, [
      navigate,
      updateUser,
    ]);

  const toggleTheme =
    useCallback(() => {
      setIsDarkMode(
        (currentValue) => {
          const nextValue =
            !currentValue;

          document.documentElement
            .classList.toggle(
              "dark",
              nextValue,
            );

          localStorage.setItem(
            "theme",
            nextValue
              ? "dark"
              : "light",
          );

          return nextValue;
        },
      );
    }, []);

  const toggleMenu =
    useCallback(() => {
      setMenuOpen(
        (currentValue) =>
          !currentValue,
      );
    }, []);

  const closeMenu =
    useCallback(() => {
      setMenuOpen(false);
    }, []);

  const getItemBadgeCount =
    useCallback(
      (badgeType) => {
        return (
          badgeCounts[
            badgeType
          ] || 0
        );
      },
      [badgeCounts],
    );

  const canAccessConvocations =
    hasAccessToItem(
      CONVOCATION_ROLES,
    );

  return {
    user,
    userRoleId,

    menuOpen,
    isDarkMode,

    pathname:
      location.pathname,

    avatarUrl,

    badgeCounts,

    pendingVotesLoading,
    loadingConvocations,

    canAccessConvocations,

    hasAccessToItem,
    getItemBadgeCount,

    handleNavigation,
    handleLogout,
    toggleTheme,
    toggleMenu,
    closeMenu,
  };
}